import {
    type FC,
    useState,
    useEffect,
    useCallback,
    createContext,
    useContext,
    type ReactNode,
} from 'react';
import { type Environment } from 'relay-runtime';
import { providers, utils } from 'ethers';
import serverVars from 'server-vars';
import { UnsupportedChainIdError, useWeb3React } from '@web3-react/core';
import {
    InjectedConnector,
    NoEthereumProviderError,
    UserRejectedRequestError,
} from '@web3-react/injected-connector';
import { WalletConnectConnector } from '@web3-react/walletconnect-connector';
import { useIntl } from 'dibs-react-intl';
import { useMetaMaskChangeListener } from './useMetaMaskChangeListener.ts';
import { useIsUnsupportedChain } from './useIsUnsupportedChain.ts';
import { modifyEthereumWalletConnection } from './mutations/modifyEthereumWalletConnection.ts';
import { signMessage } from './signMessage.ts';
import {
    chains,
    supportedChains,
    supportedChainRPC,
    getChainIdFromEnv,
    getLongChainName,
    alchemyUrlFromEnv,
} from './supportedChains.ts';

export const supportedChainIds = supportedChains();
export const supportedChainWC = supportedChainRPC();
export const injectedConnector = new InjectedConnector({ supportedChainIds });
export const walletConnectConnector = new WalletConnectConnector({
    rpc: supportedChainWC,
    qrcodeModalOptions: { desktopLinks: ['Wallet 3', 'Ledger Live'] },
});

export type InitiateConnectionResult = {
    success: boolean;
    connecting: boolean;
    noMetaMask?: boolean;
    userRejection?: boolean;
    alreadyConnected?: boolean;
    unknownError?: boolean;
    mmConnecting?: boolean;
    wcConnecting?: boolean;
};

export type RequiredChain = {
    id: number;
    name: string;
    url?: string;
};

type InitiateConnectionInput = {
    environment: Environment;
    userId: string;
    walletType?: string | null;
    zipCode?: string | null;
    isInternationalCustomer?: boolean;
    onSign?: (account: string) => void;
    forAddressAndBidderRole?: boolean;
} | null;

type DeactivateConnectionInput = {
    environment: Environment;
    userId: string;
    userSocialNetworkId: string;
};

export type DibsWeb3ConnectionContextType = {
    active: boolean;
    account?: string | null;
    library?: providers.JsonRpcProvider;
    connected: InitiateConnectionResult;
    initiateConnection: (input: InitiateConnectionInput) => Promise<void>;
    deactivateConnection: (input: DeactivateConnectionInput) => Promise<void>;
    openConnectionWithoutSigning: (onConnect?: () => void, walletType?: string) => Promise<void>;
    openConnectionForMismatch: () => Promise<void>;
    switchMetaMaskEthereumChain: () => Promise<void>;
    isMetaMaskInstalled: boolean;
    unsupportedChain: boolean;
    unsupportedWCChain: boolean;
    awaitingAddressAndBidderRole: boolean;
    chainId?: number;
    alchemyProvider?: providers.JsonRpcProvider;
    requiredChain: RequiredChain;
};

function useIsMetaMaskInstalled(): boolean {
    const hasWindowEthereum = typeof window !== 'undefined' && !!window?.ethereum;
    const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState(false);

    useEffect(() => {
        setIsMetaMaskInstalled(hasWindowEthereum);
    }, [hasWindowEthereum]);

    return isMetaMaskInstalled;
}

const DibsWeb3ConnectionContext = createContext<DibsWeb3ConnectionContextType>({
    active: false,
    account: null,
    library: undefined,
    connected: {
        connecting: false,
        success: false,
    },
    deactivateConnection: () => Promise.resolve(),
    initiateConnection: () => Promise.resolve(),
    openConnectionWithoutSigning: () => Promise.resolve(),
    openConnectionForMismatch: () => Promise.resolve(),
    switchMetaMaskEthereumChain: () => Promise.resolve(),
    isMetaMaskInstalled: false,
    unsupportedChain: false,
    unsupportedWCChain: false,
    awaitingAddressAndBidderRole: false,
    requiredChain: {
        id: 1,
        name: chains.get(1) || 'Ethereum Mainnet',
        url: undefined,
    },
});

export const DibsWeb3ConnectionProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { activate, active, account, deactivate, library, chainId } =
        useWeb3React<providers.JsonRpcProvider>();
    const intl = useIntl();
    const isMetaMaskInstalled = useIsMetaMaskInstalled();
    const unsupportedChain = useIsUnsupportedChain();
    const requiredChain: RequiredChain = {
        id: getChainIdFromEnv(),
        name: getLongChainName(),
        url: alchemyUrlFromEnv(),
    };
    useMetaMaskChangeListener();

    const [alchemyProvider, setAlchemyProvider] = useState<providers.JsonRpcProvider>();
    useEffect(() => {
        const alchemyUrl = serverVars.get('NODE_ALCHEMY_URL');
        if (alchemyUrl) {
            setAlchemyProvider(new providers.JsonRpcProvider(alchemyUrl));
        }
    }, []);
    const [initArgs, setInitArgs] = useState<InitiateConnectionInput>(null);

    const [connected, setConnected] = useState<InitiateConnectionResult>({
        success: false,
        connecting: false,
    });
    const [signing, setSigning] = useState(false);
    const [awaitingAddressAndBidderRole, setAwaitingAddressAndBidderRole] = useState(false);
    const [unsupportedWCChain, setUnsupportedWCChain] = useState(false);

    // doSign is part of initiateConnection flow. It's separated because we need to get account and library from web3-react
    const doSign = useCallback(async (): Promise<void> => {
        if (initArgs && library && account) {
            const signer = library.getSigner();
            const {
                environment,
                userId,
                zipCode,
                isInternationalCustomer,
                onSign,
                forAddressAndBidderRole,
            } = initArgs;
            try {
                setSigning(true);
                const { message, signature } = await signMessage(
                    signer,
                    userId,
                    intl,
                    'walletConnect',
                    library
                );
                const res = await modifyEthereumWalletConnection(environment, {
                    userId,
                    action: 'CONNECT',
                    accessToken: account,
                    address: account,
                    message,
                    signature,
                    zipCode,
                    isInternationalCustomer,
                });
                const transactionHash =
                    res.modifySocialNetworkConnection?.bidderRoleTransactionHash;
                const failureReason = res.modifySocialNetworkConnection?.failureReason;
                if (failureReason) {
                    if (failureReason === 'WALLET_ALREADY_CONNECTED') {
                        setConnected({
                            unknownError: false,
                            alreadyConnected: true,
                            success: false,
                            connecting: false,
                            mmConnecting: false,
                            wcConnecting: false,
                        });
                    } else {
                        setConnected({
                            unknownError: true,
                            success: false,
                            connecting: false,
                            mmConnecting: false,
                            wcConnecting: false,
                        });
                    }
                    setInitArgs(null);
                    setSigning(false);
                    setAwaitingAddressAndBidderRole(false);
                    return;
                }

                // persistedAddress gets updated in relay store, but we're still waiting for bidder role.
                if (forAddressAndBidderRole && transactionHash) {
                    setAwaitingAddressAndBidderRole(true);
                }

                if (transactionHash) {
                    const receipt = await library.waitForTransaction(transactionHash);
                    if (receipt.status === 0) {
                        throw new Error();
                    }
                }
                setConnected({
                    success: true,
                    connecting: false,
                    mmConnecting: false,
                    wcConnecting: false,
                });
                setSigning(false);
                setAwaitingAddressAndBidderRole(false);
                setInitArgs(null);
                if (onSign) {
                    onSign(account);
                }
            } catch (e) {
                setConnected({
                    unknownError: true,
                    success: false,
                    connecting: false,
                    mmConnecting: false,
                    wcConnecting: false,
                });
                setInitArgs(null);
                setSigning(false);
                setAwaitingAddressAndBidderRole(false);
                // eslint-disable-next-line no-console
                console.error('Unknown Error\n', e);
            }
        }
    }, [initArgs, library, account, intl]);

    // wait for account and library from web3-react and only then continue with signing flow
    useEffect(() => {
        if (signing || connected.success) {
            return;
        }
        if (initArgs && library && account) {
            doSign();
        }
    }, [library, account, initArgs, doSign, signing, connected.success]);

    // resets WalletConnect modal to allow user to cancel and start again
    const resetWalletConnectConnector = (): void => {
        if (walletConnectConnector && walletConnectConnector.walletConnectProvider?.wc?.uri) {
            walletConnectConnector.close();
            walletConnectConnector.walletConnectProvider = undefined;
        }
    };

    const _handleConnectionException = useCallback((e: Error): void => {
        if (e instanceof NoEthereumProviderError) {
            setConnected({
                noMetaMask: true,
                success: false,
                connecting: false,
                mmConnecting: false,
                wcConnecting: false,
            });
        } else if (e instanceof UserRejectedRequestError) {
            setConnected({
                userRejection: true,
                success: false,
                connecting: false,
                mmConnecting: false,
                wcConnecting: false,
            });
        } else if (e instanceof UnsupportedChainIdError) {
            setConnected({
                success: false,
                connecting: false,
                mmConnecting: false,
                wcConnecting: false,
            });
            setUnsupportedWCChain(true);
        } else {
            // eslint-disable-next-line no-console
            console.error('Unknown Error\n', e);
            setConnected({
                unknownError: true,
                success: false,
                connecting: false,
                mmConnecting: false,
                wcConnecting: false,
            });
        }
        resetWalletConnectConnector();
    }, []);

    // used when MM is not connected and we don't have the wallet persisted
    const initiateConnection = useCallback(
        async function (initConnectInput: InitiateConnectionInput): Promise<void> {
            try {
                const walletType = initConnectInput?.walletType || 'metamask';
                if (walletType === 'metamask') {
                    setConnected({ success: false, connecting: true, mmConnecting: true });
                    await activate(injectedConnector, undefined, true);
                } else {
                    setConnected({ success: false, connecting: true, wcConnecting: true });
                    await activate(walletConnectConnector, undefined, true);
                }
                setInitArgs(initConnectInput);
            } catch (e: $TSFixMe) {
                _handleConnectionException(e);
            }
        },
        [_handleConnectionException, activate]
    );

    // used when we have user's wallet persisted but MM is not connected
    const openConnectionWithoutSigning = async function (
        onConnect?: () => void,
        walletType: string = 'metamask'
    ): Promise<void> {
        try {
            if (walletType === 'metamask') {
                setConnected({ success: false, connecting: true, mmConnecting: true });
                await activate(injectedConnector, undefined, true);
            } else {
                setConnected({ success: false, connecting: true, wcConnecting: true });
                await activate(walletConnectConnector, undefined, true);
            }
            setConnected({
                success: true,
                connecting: false,
                mmConnecting: false,
                wcConnecting: false,
            });
            onConnect?.();
        } catch (e: $TSFixMe) {
            _handleConnectionException(e);
        }
    };

    // used when MM is connected, but user has mismatching account
    const openConnectionForMismatch = async function (): Promise<void> {
        try {
            setConnected({ success: false, connecting: true, mmConnecting: true });
            await window.ethereum.request({
                method: 'wallet_requestPermissions',
                params: [
                    {
                        eth_accounts: {},
                    },
                ],
            });
            setConnected({ success: false, connecting: false, mmConnecting: false });
        } catch (e: $TSFixMe) {
            _handleConnectionException(e);
        }
    };

    // switch Ethereum chain - used when MM is connected, but user has unsupported chain
    const switchMetaMaskEthereumChain = async function (): Promise<void> {
        try {
            setConnected({ success: false, connecting: true, mmConnecting: true });
            await window.ethereum.request({
                method: 'wallet_switchEthereumChain',
                params: [{ chainId: utils.hexValue(requiredChain.id) }],
            });
            setConnected({ success: true, connecting: false, mmConnecting: false });
        } catch (e: $TSFixMe) {
            // This error code indicates that the chain has not been added to MetaMask.
            if (e.code === 4902) {
                try {
                    await window.ethereum.request({
                        method: 'wallet_addEthereumChain',
                        params: [
                            {
                                chainId: utils.hexValue(requiredChain.id),
                                chainName: requiredChain.name,
                                rpcUrls: [requiredChain?.url],
                            },
                        ],
                    });
                    setConnected({ success: true, connecting: false, mmConnecting: false });
                } catch (error: $TSFixMe) {
                    _handleConnectionException(error);
                }
            } else {
                _handleConnectionException(e);
            }
        }
        setConnected({ success: false, connecting: false, mmConnecting: false });
    };

    // used to disconnect user from MM and delete existing social connection
    async function deactivateConnection(
        deactivationDetails: DeactivateConnectionInput
    ): Promise<void> {
        deactivate();
        walletConnectConnector.close();

        const { environment, userId, userSocialNetworkId } = deactivationDetails;
        if (userId) {
            await modifyEthereumWalletConnection(environment, {
                action: 'DISCONNECT',
                userId,
                userSocialNetworkId,
            });
        }
    }

    return (
        <DibsWeb3ConnectionContext.Provider
            value={{
                active,
                account,
                library,
                connected,
                initiateConnection,
                awaitingAddressAndBidderRole,
                deactivateConnection,
                openConnectionWithoutSigning,
                openConnectionForMismatch,
                switchMetaMaskEthereumChain,
                isMetaMaskInstalled,
                unsupportedChain,
                unsupportedWCChain,
                chainId,
                alchemyProvider,
                requiredChain,
            }}
        >
            {children}
        </DibsWeb3ConnectionContext.Provider>
    );
};

export const useWeb3Connection = (): DibsWeb3ConnectionContextType =>
    useContext(DibsWeb3ConnectionContext);
